import { updateMatchApi } from "@/api/services/main/match";
import { IconSave } from "@/assets/icons";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { queryClient } from "@/constants/queryClient";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { MatchBasicInfoForm } from "@/features/match/components/match-form/MatchBasicInfoForm";
import { MatchJobIntroductionManagerInfoForm } from "@/features/match/components/match-form/MatchJobIntroductionManagerInfoForm";
import { MatchLocationInfoForm } from "@/features/match/components/match-form/MatchLocationInfoForm";
import { MatchCustomSystemInfoForm } from "@/features/match/components/match-form/MatchCustomSystemInfoForm";
import { MatchContractInfo } from "@/features/match/components/match-info/MatchContractInfo";
import { useGetMatchSchema } from "@/features/match/hook/useGetMatchSchema";
import { TMatchForm } from "@/features/match/types/MatchForm";
import { TMatchInfo } from "@/features/match/types/MatchInfo";
import { convertMatchReq } from "@/features/match/utils/convertMatch";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { AppRoutes } from "@/routes/config";
import { FCC } from "@/types/common";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Grid, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

type TMatchFormProps = {
  defaultValues?: Partial<TMatchForm>;
};

export const MatchForm: FCC<TMatchFormProps> = ({ defaultValues }) => {
  const navigate = useNavigate();
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState<string | null>(null);

  const { matchSchema } = useGetMatchSchema();

  const methods = useForm<TMatchForm>({
    defaultValues: {
      ...defaultValues,
    },
    resolver: yupResolver(matchSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  useLocationInfo({ methods, names: { zipCode: "zipCode", province: "prefecture", municipality: "city", address: "address" } });

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(redirect);
      setRedirect(null);
    }
  }, [blockerEnabled, redirect, navigate]);

  const onSubmit = async (data: TMatchForm) => {
    await handleSaveData(data);
  };

  const handleSaveData = async (data: TMatchForm) => {
    try {
      const payload = convertMatchReq(data);
      await updateMatchApi(payload);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MATCH.FETCH_DETAIL] });
      toast.success(API_SUCCESS_MESSAGE);
      setBlockerEnabled(false);
      setRedirect(AppRoutes.match);
    } catch (e) {
      showError(e);
    }
  };

  const { handleSubmit } = methods;

  return (
    <Stack gap={1}>
      <TopPage title="紹介会社情報" enablePageBack={false} />

      <Grid
        container
        sx={{
          marginTop: 4,
        }}
      >
        <Grid item xs={12}>
          <Form methods={methods} onSubmit={onSubmit} schema={matchSchema} enableBlocker={blockerEnabled}>
            <Box
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
                  e.preventDefault();
                }
              }}
            >
              <MenuNavigate
                contents={[
                  { title: "基本情報", content: <MatchBasicInfoForm defaultValues={defaultValues ?? {}}></MatchBasicInfoForm> },
                  {
                    title: "職業紹介責任者情報",
                    content: <MatchJobIntroductionManagerInfoForm defaultValues={defaultValues ?? {}}></MatchJobIntroductionManagerInfoForm>,
                  },
                  { title: "所在地", content: <MatchLocationInfoForm defaultValues={defaultValues ?? {}}></MatchLocationInfoForm> },
                  { title: "システムカスタマイズ", content: <MatchCustomSystemInfoForm defaultValues={defaultValues ?? {}} /> },
                  { title: "契約情報", content: <MatchContractInfo defaultValues={(defaultValues as Partial<TMatchInfo>) ?? {}} /> },
                ]}
                contentWidth="900px"
                contentAffix={
                  <FooterContainer>
                    <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                      <Button size="sm" variant="ghost" onClick={() => navigate(AppRoutes.match)}>
                        戻る
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        startIcon={<IconSave />}
                        sx={{ fontWeight: 500 }}
                        onClick={handleSubmit(onSubmit)}
                      >
                        保存
                      </Button>
                    </Stack>
                  </FooterContainer>
                }
              />
            </Box>
          </Form>
        </Grid>
      </Grid>
    </Stack>
  );
};
